import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Portal from '../Portal';
import { formatDateStringToStrBar, formatTimeString } from '../../utils/formatDate';
import SmallTextLabel from '../button/SmallTextLabel';
import { BtnBlue, BtnGraySmall } from '../Button';
import { IoMap } from 'react-icons/io5';
import MapContainer from './MapViewModal';
import { downloadFile } from '../../utils/flutterBridge';
import FormatImg from '../FormatImg';

interface NotificationModalProps {
  openModal: boolean;
  setOpenModal: Dispatch<SetStateAction<{ isOpen: boolean; data: any | null }>>;
  data: {
    smGubunName: string;
    completeYn: string;
    travelYn: string;
    importantYn: string;
    smName: string;
    hName: string;
    sName: string;
    address: string;
    latitude: string;
    longitude: string;
    sDate: string;
    fDate: string;
    sTime: string;
    fTime: string;
    alldayYn: string;
    csName: string;
    userNames?: string;
    contents: string;
    isPaid: string | null;
    ccName: string;
    dName: string | null;
    wDate: string;
    writer: string;
    eDate: string;
    editor: string;
    backColor?: string;
    fontColor?: string;
    scheduleFList?: any[];
  };
}

const AppNotificationModal = ({ openModal, setOpenModal, data }: NotificationModalProps) => {
  const { t } = useTranslation();
  const [openSubModal, setOpenSubModal] = useState<any>({ status: false, type: '' });

  // 파일 다운로드
  const handleFileDownload = async (fileUrl: string, filename: string) => {
    try {
      await downloadFile(fileUrl, filename);
    } catch (err) {
      console.error('There was an error:', err);
    }
  };

  const handleClose = () => {
    setOpenModal({ isOpen: false, data: null });
  };

  useEffect(() => {
    if (openSubModal.status && openSubModal.type === 'mapView' && window.MapViewer && data?.latitude && data?.longitude) {
      window.MapViewer.postMessage(
        JSON.stringify({
          latitude: data.latitude,
          longitude: data.longitude,
          address1: data.address,
        })
      );
    }
  }, [openSubModal, data]);

  return (
    <Portal openModal={openModal}>
      <ModalStyle>
        <div className='modal-container'>
          <div className='modal-header'>
            <h3>
              <span className='material-symbols-rounded'>calendar_month</span>
              {t(`${data?.smGubunName ?? ''} 일정 내용`)}
            </h3>
            <button type='button' onClick={handleClose} className='close-button'>
              <span className='material-symbols-rounded'>close</span>
            </button>
          </div>
          <div className='modal-content'>
            <div className='badge-container'>
              {data?.importantYn === 'Y' && <SmallTextLabel text='중요' color='red' />}
              {data?.travelYn === 'Y' && <SmallTextLabel text='출장' color='blue' />}
              {data?.completeYn === 'Y' && <SmallTextLabel text='완료' color='green' />}
            </div>
            <div className='info-grid'>
              <InfoRow label={t('일정명')} value={data?.smName || '-'} />
              <InfoRow label={t('본사/현장')} value={`${data?.hName || '-'} / ${data?.sName || '-'}`} />
              <InfoRow
                label={t('주소')}
                value={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ flex: 1 }}>{data?.address || '-'}</span>
                    {data?.latitude && data?.longitude && (
                      <BtnGraySmall onClick={() => setOpenSubModal({ status: true, type: 'mapView' })}>
                        <IoMap size={14} />
                      </BtnGraySmall>
                    )}
                  </div>
                }
              />
              <InfoRow label={t('일자')} value={`${formatDateStringToStrBar(data?.sDate)} ~ ${formatDateStringToStrBar(data?.fDate)}`} />
              <InfoRow
                label={t('시간')}
                value={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>{`${formatTimeString(data?.sTime)} ~ ${formatTimeString(data?.fTime)}`}</span>
                    {data?.alldayYn === 'Y' && (
                      <div className='badge-container'>
                        <SmallTextLabel text={t('종일')} color='slate' />
                      </div>
                    )}
                  </div>
                }
              />
              <InfoRow label={t('참가자')} value={data?.userNames ? data.userNames.split('|').join(', ') : '-'} />
              <InfoRow
                label={t('공유항목')}
                value={
                  <div style={{ backgroundColor: data?.backColor || 'transparent', color: data?.fontColor || 'inherit', padding: '0.1rem 0.5rem', borderRadius: '0.25rem', width: 'fit-content' }}>
                    {data?.csName || '-'}
                  </div>
                }
              />
              <InfoRow label={t('일정내용')} value={data?.contents || '-'} isMultiLine />
              <InfoRow label={t('유/무상')} value={data?.isPaid === 'Y' ? t('유상') : t('무상')} />
              <InfoRow label={t('차량')} value={data?.ccName || '-'} />
              <InfoRow label={t('운전자')} value={data?.dName || '-'} />
              <InfoRow
                label={t('첨부파일')}
                value={
                  data?.scheduleFList && data.scheduleFList.length > 0 ? (
                    <div className='file-wrap'>
                      <div className='file-inner'>
                        {data.scheduleFList.map((file: { fUrl: string }, index: number) => {
                          // URL에서 파일명 추출
                          const fileName = file.fUrl.split('/').pop() || '';
                          // URL 디코딩하여 실제 파일명 얻기
                          const decodedFileName = decodeURIComponent(fileName);
                          // 파일 확장자 추출
                          const fileFormat = decodedFileName.split('.').pop() || '';

                          return (
                            <div key={index} className='file-item'>
                              <div className='file'>
                                <FormatImg format={fileFormat} />
                                <span>{decodedFileName}</span>
                              </div>
                              <div className='flex-basic' style={{ gap: '0.5rem' }}>
                                {file.fUrl && (
                                  <BtnBlue className='file-btn' onClick={() => handleFileDownload(file.fUrl, decodedFileName)}>
                                    <span className='material-symbols-rounded'>download</span>
                                  </BtnBlue>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    '-'
                  )
                }
              />
              <InfoRow label={t('등록자')} value={`${data?.writer ?? '-'} (${dayjs(data?.wDate).format('YYYY-MM-DD HH:mm')})`} />
              <InfoRow label={t('수정자')} value={data?.editor ? `${data?.editor ?? '-'} (${dayjs(data?.eDate).format('YYYY-MM-DD HH:mm')})` : '-'} />
            </div>
          </div>
          <div className='modal-footer'>
            <button type='button' onClick={handleClose} className='close'>
              <span className='material-symbols-rounded'>check</span>
              {t('확인')}
            </button>
          </div>
        </div>
      </ModalStyle>
      {!window.FlutterWebView && openSubModal.status && openSubModal.type === 'mapView' && (
        <MapContainer
          openModal={openSubModal}
          setOpenModal={setOpenSubModal}
          address={{
            address1: data?.address || '',
            latitude: data?.latitude || '',
            longitude: data?.longitude || '',
            postNum: '',
          }}
        />
      )}
    </Portal>
  );
};

const InfoRow: React.FC<{ label: string; value: any; isMultiLine?: boolean }> = ({ label, value, isMultiLine }) => (
  <>
    <div className='label'>{label}</div>
    <div className={`value ${isMultiLine ? 'multiline' : ''}`}>{value}</div>
  </>
);

const ModalStyle = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100dvh;
  background-color: rgba(0, 0, 0, 60%);
  backdrop-filter: blur(0.5rem);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5010;
  user-select: none;

  .modal-container {
    background: var(--board);
    border-radius: 0.5rem;
    width: 40rem;
    max-width: calc(100dvw - 2rem);
    max-height: 80dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: var(--filled-blue-light);
    color: var(--tonal);
    min-height: 3.5rem;
    padding: 0.75rem 1rem;

    h3 {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0;
      font-size: 1.125rem;
      font-weight: 400;

      .material-symbols-rounded {
        font-variation-settings: 'FILL' 0, 'wght' 300, 'GRAD' 0, 'opsz' 24;
        font-size: 1.5rem;
      }
    }

    .close-button {
      border-radius: 0.5rem;
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      background: none;
      border: none;
      margin-right: -1rem;

      .material-symbols-rounded {
        font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
        font-size: 1.375rem;
        color: var(--alpha-50);
      }

      &:hover {
        background-color: var(--filled-red);
        box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);

        .material-symbols-rounded {
          color: var(--tonal-deep-red);
        }
      }
    }
  }

  .modal-content {
    padding: 0.75rem;
    overflow-y: auto;
    background-color: var(--board);
    max-height: calc(100dvh - 10rem);

    .badge-container {
      display: flex;
      gap: 5px;
      justify-content: flex-end;
      align-items: center;

      .badge {
        height: fit-content;
        margin: 0;
        padding: 0 0.5rem;
      }
    }

    .info-grid {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0.75rem;
      align-items: start;
      padding: 0.5rem 0;

      .label {
        color: var(--text-tertiary);
        font-weight: 500;
        padding-right: 1.5rem;
        white-space: nowrap;
        font-size: 0.875rem;
      }

      .value {
        color: var(--text-primary);

        &.multiline {
          white-space: pre-wrap;
          grid-column: span 2;
          padding: 0.75rem;
          background: var(--tonal);
          border-radius: 0.5rem;
        }
      }

      .file-wrap {
        width: 100%;
        border-radius: 0.3rem;
        border: 1px solid var(--outline);

        .file-inner {
          display: flex;
          flex-direction: column !important;
          width: 100%;
        }

        .file-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 0.5rem;

          &:hover {
            background-color: var(--tonal);
          }
        }

        .file {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          min-width: 65%;

          svg {
            flex: 1;
            width: 1.5rem;
            height: 1.5rem;
          }

          span {
            width: calc(100% - 36px);
            font-size: 0.875rem;
            color: var(--text-primary);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .file-btn {
          width: auto;
          height: 1.8rem;
          padding: 0 0.5rem;

          span.material-symbols-rounded {
            font-variation-settings: 'FILL' 1, 'wght' 400;
            font-size: 1rem;
          }
        }
      }
    }
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--outline);
    min-height: 3.5rem;

    button {
      cursor: pointer;
      color: var(--filled-red-deep);
      padding: 0 1.5rem 0 0.5rem;
      height: 2.25rem;
      border-radius: 2rem;
      font-size: 0.938rem;
      letter-spacing: normal;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      transition: all 0.05s ease-in-out;
      background-color: var(--tonal-red);
      outline: 1px solid var(--tonal-deep-red);
      border: none;

      .material-symbols-rounded {
        background-color: var(--tonal-deep-red);
        border-radius: 1rem;
        padding: 0.125rem;
        font-size: 1.375rem;
        transition: all 0.25s ease-in-out;
      }

      &:hover {
        .material-symbols-rounded {
          font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
          background-color: var(--filled-red);
          color: var(--tonal-deep-red);
        }
      }
    }
  }
`;

export default AppNotificationModal;
